import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../icons';

// Toasts: transient feedback for every mutation. Success can carry an Undo action; errors
// linger. Screen readers hear them via role=status / role=alert. One provider, one stack.

export interface ToastInput {
  kind?: 'success' | 'error' | 'info';
  title: string;
  detail?: string;
  /** Optional action (e.g. Undo). Runs then dismisses. */
  action?: { label: string; run: () => void | Promise<void> };
  /** ms before auto-dismiss. Defaults: success/info 4500, error 8000. 0 = sticky. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
  kind: NonNullable<ToastInput['kind']>;
  leaving?: boolean;
}

interface ToastApi {
  push: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

export function useToast(): (t: ToastInput) => number {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast requires <ToastProvider>');
  return ctx.push;
}

export function useToastApi(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToastApi requires <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) window.clearTimeout(t);
    timers.current.delete(id);
    setToasts((ts) => ts.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
    window.setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), 180);
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      const kind = input.kind ?? 'success';
      const duration = input.duration ?? (kind === 'error' ? 8000 : 4500);
      setToasts((ts) => [...ts.slice(-3), { ...input, kind, id }]);
      if (duration > 0) timers.current.set(id, window.setTimeout(() => dismiss(id), duration));
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="hf-toasts" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <div key={t.id} className={['hf-toast', t.kind, t.leaving && 'leaving'].filter(Boolean).join(' ')} role={t.kind === 'error' ? 'alert' : 'status'}>
            <Icon name={t.kind === 'error' ? 'alert-circle' : t.kind === 'info' ? 'info' : 'check-circle'} size={15} style={{ marginTop: 2, color: `var(--${t.kind === 'error' ? 'neg' : t.kind === 'info' ? 'accent' : 'pos'})` }} />
            <div className="body">
              <div className="title">{t.title}</div>
              {t.detail && <div className="detail">{t.detail}</div>}
            </div>
            {t.action && (
              <button
                className="action"
                onClick={() => {
                  void t.action!.run();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            <button className="close" aria-label="Dismiss" onClick={() => dismiss(t.id)}>
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
